import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as UIcons from '@iconscout/react-unicons'
import Btn, { FormBtn } from 'components/Btn'
import Input from 'components/Input'
import PhoneNumberInput from 'components/PhoneNumberInput'
import { apiRequest } from 'store/Misc/apiRequest'
import { setFormLoading } from 'store/slices/miscSlice'

export default withTranslation()(function EditProfile({ t, user, handleClick }) {
  const dispatch = useDispatch()
  const method = useForm({ mode: 'all' })
  const { register, reset, handleSubmit } = method

  useEffect(() => {
    if (user) reset({ ...user })
  }, [user, reset])

  const submit = async (payload) => {
    const extract = Object.fromEntries(
      ['first_name', 'last_name', 'job_position', 'phone', 'email'].map((key) => [key, payload[key]])
    )

    if (!Object.values(extract).every((value) => value)) {
      toast.warn(t('settings.account_security.edit_profile.fill_all'))
      return
    }

    dispatch(setFormLoading())
    try {
      await apiRequest({ url: 'settings/contact-person', method: 'post', payload: extract })
      toast.success(t('settings.account_security.edit_profile.success'))
      handleClick()
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(setFormLoading('close'))
    }
  }

  const roleName = user?.roles?.[0]?.display_name ?? user?.roles?.[0]?.name ?? ''

  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4" onClick={handleClick}>
      <div className="bg-white relative w-11/12 max-w-md rounded-xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(event) => event.stopPropagation()}>
        <div className="p-6 md:p-8 pb-0 shrink-0">
          <div className="relative flex items-center justify-center mb-6">
            <Btn
              className="w-10 h-10 hover:bg-default absolute left-0"
              content={() => <UIcons.UilTimes className="text-secondary" />}
              click={handleClick}
            />

            <h3 className="text-xl font-bold text-dark-blue">{t('settings.account_security.edit_profile.title')}</h3>
          </div>

          <div className="w-full h-1.5 bg-primary rounded-full mb-6" />
        </div>

        <div className="overflow-y-auto px-6 md:px-8 pb-6 md:pb-8">
          <FormProvider {...method}>
            <form onSubmit={handleSubmit(submit)}>
              <div className="flex justify-center mb-6">
                <span className="inline-flex rounded-full overflow-hidden bg-primary w-20 h-20 shrink-0 border border-border-lines">
                  {
                    user?.avatar ? (
                      <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-lg font-bold text-white m-auto">
                        {`${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`}
                      </span>
                    )
                  }
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mb-3">
                <Input
                  label={t('settings.user_management.form.first_name.label')}
                  placeholder={t('settings.user_management.form.first_name.placeholder')}
                  variant="outlined"
                  {...register('first_name')}
                />

                <Input
                  label={t('settings.user_management.form.last_name.label')}
                  placeholder={t('settings.user_management.form.last_name.placeholder')}
                  variant="outlined"
                  {...register('last_name')}
                />
              </div>

              <div className="mb-3">
                <Input
                  label={t('settings.user_management.form.work_email.label')}
                  placeholder={t('settings.user_management.form.work_email.placeholder')}
                  variant="outlined"
                  {...register('email')}
                />
              </div>

              <div className="mb-3">
                <Input
                  label={t('settings.account_security.edit_profile.job_title')}
                  variant="outlined"
                  {...register('job_position')}
                />
              </div>

              <div className="mb-3">
                <PhoneNumberInput
                  name="phone"
                  label={t('settings.user_management.form.phone.label')}
                  className={{}}
                  singleInputClassName="w-full bg-white rounded-lg py-3"
                  country="NG"
                  code="234"
                />
              </div>

              <div className="mb-6">
                <label className="text-secondary text-sm inline-block mb-2">{t('settings.account_security.edit_profile.role')}</label>
                <div className="w-full bg-background border border-border-lines rounded-xl px-4 py-3 text-base text-secondary">
                  {roleName}
                </div>
              </div>

              <FormBtn content={t('misc.save')} className="w-full h-12 rounded-xl bg-primary text-white" />
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
})
